import { appendVariableTrace } from './sstVariableTrace.js';
import { getSstSystemValue } from './sstSystemValues.js';

const MAX_AUTOMATIC_STEPS = 1000;
const COMMAND_BUFFER_KEY = '__SST_COMMAND_BUFFER_TEMPLATE';

function normalizeName(value = '') {
  return String(value)
    .trim()
    .replace(/^\[|\]$/g, '')
    .replace(/^@/, '')
    .replace(/#[A-Z]$/i, '')
    .replace(/:$/, '')
    .trim()
    .toUpperCase();
}

function cloneFrames(frames = []) {
  return frames.map((frame) => ({ ...frame, actions: [...(frame.actions || [])] }));
}

function normalizeBlockName(value = '') {
  return String(value)
    .trim()
    .replace(/^\[|\]$/g, '')
    .replace(/^(?:CALL|GOTO|SUBROUTINE|SUB)\s+/i, '')
    .replace(/:+$/, '')
    .trim()
    .toUpperCase();
}

function resolveBlockEntry(program = {}, target = '') {
  const wanted = normalizeBlockName(target);
  if (!wanted) return null;
  const match = Object.entries(program.blocks || {})
    .find(([name, actions]) => normalizeBlockName(name) === wanted && Array.isArray(actions));
  return match ? { name: normalizeBlockName(match[0]), actions: match[1] } : null;
}

function makeBlockFrame(program, target) {
  const block = resolveBlockEntry(program, target);
  if (!block) return null;
  return { kind: 'block', name: block.name, actions: block.actions, index: 0 };
}

function makeInlineFrame(actions, name = 'inline') {
  return { kind: 'inline', name, actions: Array.isArray(actions) ? actions : [], index: 0 };
}

function getValue(values, reference) {
  const wanted = normalizeName(reference);
  const entry = Object.entries(values || {}).find(([key]) => normalizeName(key) === wanted);
  if (entry) return entry[1];
  return getSstSystemValue(reference) ?? '';
}

function setValue(values, reference, value) {
  const rawTarget = String(reference || '');
  const target = rawTarget.replace(/^@/, '').replace(/#[A-Z]$/i, '');
  if (!target) return;
  const wanted = normalizeName(target);
  const existingKeys = Object.keys(values || {}).filter((key) => normalizeName(key) === wanted);
  if (existingKeys.length > 0) existingKeys.forEach((key) => { values[key] = value; });
  else values[target] = value;
  if (rawTarget.startsWith('@')) values[rawTarget] = value;
}

function interpolate(template = '', values = {}) {
  return String(template).replace(/\{([^}]+)\}|\[([^\]]+)\]/g, (match, brace, bracket) => {
    const reference = brace || bracket;
    if (brace && ['CLEAR', 'CLEAR_ALL', 'RESET', 'ENTER', 'TAB', 'ESC'].includes(normalizeName(reference))) return match;
    return getValue(values, reference);
  });
}

export function stripSstControlTokens(command = '') {
  return String(command)
    .replace(/\{(?:CLEAR|CLEAR_ALL|RESET|ENTER|TAB|ESC)\}/gi, '')
    .replace(/\u0001/g, '\u00a4')
    .replace(/\u0017/g, '\u00a7')
    .replace(/[\u0000-\u001f\u007f-\u009c\u009e-\u009f]/g, '')
    .trim();
}

export function splitSstCommands(template = '') {
  return String(template)
    .split(/\u0015+/)
    .map((command) => stripSstControlTokens(command))
    .filter(Boolean);
}

export function previewSstCommands(action = {}, values = {}) {
  if (!['command', 'remark', 'ssr'].includes(action.type)) return [];
  return splitSstCommands(interpolate(action.template || '', values));
}

function actionDiagnostic(frame, action, actionIndex, extra = {}) {
  return {
    kind: extra.kind || 'engine-blocked',
    message: extra.message || 'El motor SST no pudo continuar automaticamente.',
    block: frame?.name || '',
    actionIndex: Number.isInteger(actionIndex) ? actionIndex : null,
    actionType: action?.type || '',
    actionId: action?.actionId || '',
    sourceLine: action?.sourceLine ?? action?.line ?? null,
    raw: action?.raw || '',
    target: extra.target ?? action?.target ?? action?.legacyTarget ?? '',
    missingVariables: extra.missingVariables || [],
    ...extra,
  };
}

function hasKnownEmptyRead(values, reference) {
  const wanted = normalizeName(reference);
  const trace = Array.isArray(values?.__SST_VARIABLE_TRACE) ? values.__SST_VARIABLE_TRACE : [];
  const assignments = trace.filter((entry) => normalizeName(entry?.variable) === wanted);
  const lastAssignment = assignments[assignments.length - 1];
  return lastAssignment?.type === 'read' && lastAssignment.empty === true;
}

function missingConditionVariables(conditions = [], values = {}) {
  const missing = new Set();
  conditions.forEach((condition) => {
    if (!condition || condition.join) return;
    const operator = String(condition.operator || '=').trim().toUpperCase();
    const comparesWithEmptyLiteral = condition.rightType !== 'variable'
      && ['=', '==', 'EQ', '!=', '<>', 'NE'].includes(operator)
      && String(cleanLiteral(condition.right)) === '';
    if (condition.left && getValue(values, condition.left) === '' && !comparesWithEmptyLiteral && !hasKnownEmptyRead(values, condition.left)) {
      missing.add(normalizeName(condition.left));
    }
    if (condition.rightType === 'variable' && condition.right && getValue(values, condition.right) === '' && !hasKnownEmptyRead(values, condition.right)) {
      missing.add(normalizeName(condition.right));
    }
  });
  return [...missing].filter(Boolean);
}

function commandTemplateVariables(template = '') {
  const variables = [];
  String(template).replace(/\{([^}]+)\}|\[([^\]]+)\]/g, (_match, brace, bracket) => {
    const reference = brace || bracket;
    const name = normalizeName(reference);
    if (!name || ['CLEAR', 'CLEAR_ALL', 'RESET', 'ENTER', 'TAB', 'ESC'].includes(name)) return '';
    variables.push(name);
    return '';
  });
  return [...new Set(variables)];
}

function isLocalEmulatorControl(action = {}) {
  const template = String(action.template || '');
  if (!/\{(?:CLEAR|CLEAR_ALL|RESET|ENTER|TAB|ESC)\}/i.test(template)) return false;
  const executable = template
    .replace(/\{(?:CLEAR|CLEAR_ALL|RESET|ENTER|TAB|ESC)\}/gi, '')
    .replace(/[\u0000-\u001f\u007f-\u009c\u009e-\u009f]/g, '')
    .trim();
  return executable === '';
}

function missingCommandVariables(action = {}, values = {}) {
  return commandTemplateVariables(action.template)
    .filter((name) => getValue(values, name) === '');
}

function missingWriteVariables(action = {}, values = {}) {
  return commandTemplateVariables(action.expression ?? action.template)
    .filter((name) => getValue(values, name) === '');
}

function resolveComputeExpression(expression = '', values = {}) {
  return String(expression)
    .replace(/\[([^\]]+)\]|\{([^}]+)\}/g, (_match, bracket, brace) => getValue(values, bracket || brace))
    .replace(/@?[A-Z_][A-Z0-9_#]*/gi, (reference) => getValue(values, reference));
}

function evaluateArithmeticExpression(expression = '') {
  const source = String(expression).replace(/(\d),(?=\d)/g, '$1.').replace(/\s+/g, '');
  if (!source || !/^[0-9.+\-*/()]+$/.test(source)) return null;
  const tokens = source.match(/(?:\d+(?:\.\d*)?|\.\d+)|[()+\-*/]/g) || [];
  if (tokens.join('') !== source) return null;
  let index = 0;

  const parseFactor = () => {
    const token = tokens[index];
    if (token === '+' || token === '-') {
      index += 1;
      const value = parseFactor();
      return token === '-' ? -value : value;
    }
    if (token === '(') {
      index += 1;
      const value = parseExpression();
      if (tokens[index] !== ')') throw new Error('parentesis incompleto');
      index += 1;
      return value;
    }
    if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(token || '')) throw new Error('numero esperado');
    index += 1;
    return Number(token);
  };
  const parseTerm = () => {
    let value = parseFactor();
    while (tokens[index] === '*' || tokens[index] === '/') {
      const operator = tokens[index++];
      const right = parseFactor();
      value = operator === '*' ? value * right : value / right;
    }
    return value;
  };
  const parseExpression = () => {
    let value = parseTerm();
    while (tokens[index] === '+' || tokens[index] === '-') {
      const operator = tokens[index++];
      const right = parseTerm();
      value = operator === '+' ? value + right : value - right;
    }
    return value;
  };

  try {
    const result = parseExpression();
    return index === tokens.length && Number.isFinite(result) ? result : null;
  } catch (_) {
    return null;
  }
}

function splitConcatenationExpression(expression = '') {
  const parts = [];
  let current = '';
  let quote = '';
  let depth = 0;
  for (const char of String(expression)) {
    if ((char === '"' || char === "'") && (!quote || quote === char)) {
      quote = quote ? '' : char;
      current += char;
      continue;
    }
    if (!quote && char === '(') depth += 1;
    if (!quote && char === ')') depth = Math.max(0, depth - 1);
    if (!quote && depth === 0 && char === '+') {
      parts.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }
  parts.push(current.trim());
  return parts;
}

function evaluateComputeExpression(expression = '', values = {}) {
  const source = String(expression).trim();
  const quoted = source.match(/^(["'])([\s\S]*)\1$/);
  if (quoted) return quoted[2];
  if (/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(source)) return Number(source);
  if (/^@?[A-Z_][A-Z0-9_#]*$/i.test(source)) return getValue(values, source);

  const resolved = resolveComputeExpression(source, values);
  const arithmetic = evaluateArithmeticExpression(resolved);
  if (arithmetic !== null) return arithmetic;

  const parts = splitConcatenationExpression(source);
  if (parts.length > 1) {
    return parts.map((part) => {
      const literal = part.match(/^(["'])([\s\S]*)\1$/);
      if (literal) return literal[2];
      const reference = part.match(/^\[([^\]]+)\]$|^\{([^}]+)\}$/);
      if (reference) return getValue(values, reference[1] || reference[2]);
      return resolveComputeExpression(part, values);
    }).join('');
  }
  return resolved;
}

function computeExpressionVariables(expression = '') {
  const withoutLiterals = String(expression).replace(/(["'])(?:\\.|(?!\1)[\s\S])*\1/g, ' ');
  return [...new Set(
    (withoutLiterals.match(/@?[A-Z_][A-Z0-9_#]*/gi) || []).map(normalizeName).filter(Boolean)
  )];
}

function missingComputeVariables(action = {}, values = {}) {
  const expression = String(action.expression ?? '').trim();
  const references = computeExpressionVariables(expression);
  if (references.length === 1 && references[0] === normalizeName(action.target)) return [];
  return references.filter((reference) => getValue(values, reference) === '');
}

function variableDefinition(definitions = {}, reference = '') {
  const wanted = normalizeName(reference);
  return Object.entries(definitions || {})
    .find(([name]) => normalizeName(name) === wanted)?.[1] || null;
}

function normalizeComputedValue(value, target, definitions = {}) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return value;
  const datatype = String(variableDefinition(definitions, target)?.datatype || '').trim().toUpperCase();

  if (['N', 'INT', 'INTEGER', 'NUM', 'NUMBER', 'NUMERIC'].includes(datatype)) {
    return Math.round(value);
  }
  if (['DEC', 'DECIMAL'].includes(datatype)) {
    return Number(value.toPrecision(15));
  }
  return Number(value.toPrecision(15));
}

function applyLocalAction(action, values, definitions = {}) {
  if (action.type === 'default' && action.target) {
    if (action.defaultType === 'default_external_catalog') return true;
    if (action.isSelfAssignment) return true;
    const option = action.options?.[0];
    const value = action.valueType === 'variable'
      ? getValue(values, action.value)
      : action.value ?? option?.value ?? option?.text ?? getValue(values, action.target);
    setValue(values, action.target, value ?? '');
    return true;
  }

  if (action.type === 'compute' && action.target) {
    const expression = String(action.expression ?? '').trim();
    const value = normalizeComputedValue(
      evaluateComputeExpression(expression, values),
      action.target,
      definitions,
    );
    setValue(values, action.target, value);
    return true;
  }

  return false;
}

function cleanLiteral(value) {
  const text = String(value ?? '').trim();
  if ((text.startsWith('"') && text.endsWith('"')) || (text.startsWith("'") && text.endsWith("'"))) return text.slice(1, -1);
  return value ?? '';
}

function compare(leftValue, operator, rightValue) {
  const left = String(leftValue ?? '').trim();
  const right = String(rightValue ?? '').trim();
  const normalizedOperator = String(operator || '=').trim().toUpperCase();
  const numericPattern = /^[+-]?(?:\d+(?:[.,]\d+)?|[.,]\d+)$/;
  const numericLeft = numericPattern.test(left) ? Number(left.replace(',', '.')) : Number.NaN;
  const numericRight = numericPattern.test(right) ? Number(right.replace(',', '.')) : Number.NaN;
  const numeric = Number.isFinite(numericLeft) && Number.isFinite(numericRight);
  const decimalNumeric = numeric && (/[.,]/.test(left) || /[.,]/.test(right));

  if (['=', '==', 'EQ'].includes(normalizedOperator)) return decimalNumeric ? numericLeft === numericRight : left.toUpperCase() === right.toUpperCase();
  if (['!=', '<>', 'NE'].includes(normalizedOperator)) return decimalNumeric ? numericLeft !== numericRight : left.toUpperCase() !== right.toUpperCase();
  if (normalizedOperator === 'CONTAINS') return left.toUpperCase().includes(right.toUpperCase());
  if (normalizedOperator === 'MATCHES') {
    try { return new RegExp(right, 'i').test(left); } catch { return false; }
  }

  const comparableLeft = numeric ? numericLeft : left.toUpperCase();
  const comparableRight = numeric ? numericRight : right.toUpperCase();
  if (normalizedOperator === '>') return comparableLeft > comparableRight;
  if (normalizedOperator === '<') return comparableLeft < comparableRight;
  if (normalizedOperator === '>=') return comparableLeft >= comparableRight;
  if (normalizedOperator === '<=') return comparableLeft <= comparableRight;
  return false;
}

function conditionEvaluation(conditions = [], values = {}) {
  if (!conditions.length) return { matched: false, details: [] };

  const operands = [];
  const operators = [];
  let pendingJoin = 'AND';
  let seenCondition = false;
  const details = [];
  const precedence = (operator) => (operator === 'AND' ? 2 : 1);
  const applyOperator = () => {
    const operator = operators.pop();
    const right = Boolean(operands.pop());
    const left = Boolean(operands.pop());
    operands.push(operator === 'OR' ? left || right : left && right);
  };

  conditions.forEach((condition) => {
    if (condition.join) {
      pendingJoin = String(condition.join).toUpperCase();
      return;
    }

    const left = getValue(values, condition.left);
    const right = condition.rightType === 'variable' ? getValue(values, condition.right) : cleanLiteral(condition.right);
    const current = compare(left, condition.operator, right);
    const join = seenCondition ? pendingJoin : '';

    if (seenCondition) {
      while (operators.length && operators[operators.length - 1] !== '(' && precedence(operators[operators.length - 1]) >= precedence(pendingJoin)) {
        applyOperator();
      }
      operators.push(pendingJoin);
    }
    for (let count = 0; count < Number(condition.groupOpen || 0); count += 1) operators.push('(');
    operands.push(current);
    for (let count = 0; count < Number(condition.groupClose || 0); count += 1) {
      while (operators.length && operators[operators.length - 1] !== '(') applyOperator();
      if (operators[operators.length - 1] === '(') operators.pop();
    }

    details.push({
      join,
      left: normalizeName(condition.left),
      leftValue: left,
      operator: condition.operator || '=',
      right: condition.rightType === 'variable' ? normalizeName(condition.right) : cleanLiteral(condition.right),
      rightValue: right,
      rightType: condition.rightType || 'literal',
      matched: current,
      groupOpen: Number(condition.groupOpen || 0),
      groupClose: Number(condition.groupClose || 0),
    });

    seenCondition = true;
    pendingJoin = 'AND';
  });

  while (operators.length) {
    if (operators[operators.length - 1] === '(') operators.pop();
    else applyOperator();
  }
  return { matched: Boolean(operands.pop()), details };
}

function splitLogicalConditions(expression = '') {
  const source = String(expression);
  const tokens = [];
  let current = '';
  let quote = '';
  let escaped = false;

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];
    if (quote) {
      current += character;
      if (escaped) escaped = false;
      else if (character === '\\') escaped = true;
      else if (character === quote) quote = '';
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
      current += character;
      continue;
    }
    const logical = source.slice(index).match(/^\s+(AND|OR)\s+/i);
    if (logical) {
      tokens.push(current.trim(), logical[1].toUpperCase());
      current = '';
      index += logical[0].length - 1;
      continue;
    }
    current += character;
  }

  if (current.trim() || tokens.length === 0) tokens.push(current.trim());
  return tokens;
}

function unwrapConditionGrouping(segment = '') {
  let text = String(segment).trim();
  let groupOpen = 0;
  let groupClose = 0;
  while (text.startsWith('(')) {
    groupOpen += 1;
    text = text.slice(1).trimStart();
  }
  while (text.endsWith(')')) {
    groupClose += 1;
    text = text.slice(0, -1).trimEnd();
  }
  return { text, groupOpen, groupClose };
}

function parseRuntimeConditions(expression = '') {
  const tokens = splitLogicalConditions(String(expression).trim());
  const conditions = [];

  tokens.forEach((token, index) => {
    if (index % 2 === 1) {
      conditions.push({ join: String(token).toUpperCase() });
      return;
    }

    const grouping = unwrapConditionGrouping(token);
    const match = grouping.text.match(/^\s*\[([^\]]+)\]\s*(<>|!=|==|<=|>=|=|<|>|EQ|NE|CONTAINS|MATCHES)\s*(?:"([^"]*)"|'([^']*)'|\[([^\]]+)\]|(.+?))\s*$/i);
    if (!match) return;
    conditions.push({
      left: match[1],
      operator: match[2],
      right: match[5] ?? match[3] ?? match[4] ?? String(match[6] ?? '').trim(),
      rightType: match[5] !== undefined ? 'variable' : 'literal',
      groupOpen: grouping.groupOpen || undefined,
      groupClose: grouping.groupClose || undefined,
    });
  });

  return conditions;
}

function requireSpec(action = {}) {
  const match = String(action.expression || '').match(/^(.*?)\s+FOR\s+\[([^\]]+)\]\s*$/i);
  if (!match) return null;
  const conditions = parseRuntimeConditions(match[1]);
  if (!conditions.some((condition) => !condition.join)) return null;
  return { conditions, target: match[2] };
}

function hasRequiredValue(value) {
  if (value === undefined || value === null || value === false) return false;
  const normalized = String(value).trim().toUpperCase();
  return normalized !== '' && normalized !== 'FALSE';
}

export function evaluateConditions(conditions = [], values = {}) {
  return conditionEvaluation(conditions, values).matched;
}

export function createNavigationCursor(program = {}) {
  const entryFrame = makeBlockFrame(program, program.entryBlock);
  return { frames: entryFrame ? [entryFrame] : [], finished: !entryFrame, steps: 0 };
}

export function initializeNavigationValues(program = {}, values = {}) {
  if (values.__SST_INITIALIZED === true) return { ...values };

  const nextValues = { ...values, __SST_INITIALIZED: true };
  (program.initializers || []).forEach((action) => {
    if (action?.type !== 'default' || !action.target) return;
    applyLocalAction(action, nextValues, program.variableDefinitions);
    appendVariableTrace(nextValues, action.target, {
      type: 'default',
      block: 'DEFAULTS',
      sourceLine: action.sourceLine ?? action.line ?? null,
      actionId: action.actionId,
      value: getValue(nextValues, action.target),
      initialization: true,
    });
  });
  return nextValues;
}

export function injectActions(cursor, actions, name = 'event') {
  return {
    ...cursor,
    finished: false,
    frames: [...cloneFrames(cursor?.frames), makeInlineFrame(actions, name)],
  };
}

function jumpToBlock(program, frames, target) {
  const targetFrame = makeBlockFrame(program, target);
  if (!targetFrame) return false;

  let blockIndex = frames.length - 1;
  while (blockIndex >= 0 && frames[blockIndex].kind !== 'block') blockIndex -= 1;
  if (blockIndex < 0) frames.splice(0, frames.length, targetFrame);
  else frames.splice(blockIndex, frames.length - blockIndex, targetFrame);
  return true;
}

function returnFromBlock(frames) {
  let blockIndex = frames.length - 1;
  while (blockIndex >= 0 && frames[blockIndex].kind !== 'block') blockIndex -= 1;
  if (blockIndex < 0) return null;
  const blockName = frames[blockIndex].name;
  frames.splice(blockIndex, frames.length - blockIndex);
  return blockName;
}

export function advanceNavigation(program = {}, sourceCursor, values = {}) {
  const initialCursor = sourceCursor || createNavigationCursor(program);
  const cursor = { ...initialCursor, frames: cloneFrames(initialCursor.frames), finished: false };
  const nextValues = initializeNavigationValues(program, values);
  const events = [];

  for (let automaticSteps = 0; automaticSteps < MAX_AUTOMATIC_STEPS; automaticSteps += 1) {
    const frame = cursor.frames[cursor.frames.length - 1];
    if (!frame) return { cursor: { ...cursor, finished: true }, values: nextValues, finished: true, events };
    if (frame.index >= frame.actions.length) {
      cursor.frames.pop();
      continue;
    }

    const cursorBeforeAction = { ...cursor, frames: cloneFrames(cursor.frames) };
    const actionIndex = frame.index;
    const action = frame.actions[actionIndex];
    frame.index += 1;
    cursor.steps += 1;
    if (!action?.type) {
      return {
        cursor,
        retryCursor: cursorBeforeAction,
        skipCursor: cursor,
        values: nextValues,
        finished: false,
        diagnostic: actionDiagnostic(frame, action, actionIndex, {
          kind: 'unsupported-action',
          message: 'El SST contiene una accion sin tipo y no puede ejecutarse de forma segura.',
        }),
        events,
      };
    }

    if (['showWindow', 'window', 'showMenu'].includes(action.type)) {
      delete nextValues.BTN_PRSD;
      delete nextValues['@BTN_PRSD'];
      events.push({ type: action.type, target: action.target || action.legacyTarget, actionId: action.actionId });
      return { cursor, retryCursor: cursorBeforeAction, skipCursor: cursor, values: nextValues, finished: false, screenRequest: action, events };
    }
    if (action.type === 'exit' || action.type === 'stop') {
      events.push({ type: 'exit', actionId: action.actionId });
      return { cursor: { ...cursor, frames: [], finished: true }, values: nextValues, finished: true, events };
    }
    if (action.type === 'return') {
      const target = returnFromBlock(cursor.frames);
      events.push({ type: 'return', target, actionId: action.actionId, sourceLine: action.sourceLine, block: frame.name });
      continue;
    }
    if (action.type === 'goto') {
      const resolved = jumpToBlock(program, cursor.frames, action.target);
      events.push({ type: 'goto', target: action.target, resolved, actionId: action.actionId, sourceLine: action.sourceLine, block: frame.name });
      if (!resolved) {
        return {
          cursor,
          retryCursor: cursorBeforeAction,
          skipCursor: cursor,
          values: nextValues,
          finished: false,
          unresolvedTarget: action.target,
          diagnostic: actionDiagnostic(frame, action, actionIndex, {
            kind: 'unresolved-target',
            message: `GOTO no encontro el bloque ${action.target || '(sin destino)'}.`,
          }),
          events,
        };
      }
      continue;
    }
    if (action.type === 'call') {
      const targetFrame = makeBlockFrame(program, action.target);
      const resolved = Boolean(targetFrame);
      events.push({ type: 'call', target: action.target, resolved, actionId: action.actionId, sourceLine: action.sourceLine, block: frame.name });
      if (targetFrame) cursor.frames.push(targetFrame);
      else {
        return {
          cursor,
          retryCursor: cursorBeforeAction,
          skipCursor: cursor,
          values: nextValues,
          finished: false,
          unresolvedTarget: action.target,
          diagnostic: actionDiagnostic(frame, action, actionIndex, {
            kind: 'unresolved-target',
            message: `CALL no encontro el bloque ${action.target || '(sin destino)'}.`,
          }),
          events,
        };
      }
      continue;
    }
    if (action.type === 'if') {
      const parsedConditions = (action.conditions || []).filter((condition) => condition && !condition.join);
      if (parsedConditions.length === 0 || action.conditionParseErrors?.length) {
        const invalidText = (action.conditionParseErrors || []).join(' · ');
        return {
          cursor,
          retryCursor: cursorBeforeAction,
          skipCursor: cursor,
          values: nextValues,
          finished: false,
          diagnostic: actionDiagnostic(frame, action, actionIndex, {
            kind: 'condition-unparsed',
            message: `La condicion no pudo interpretarse${invalidText ? `: ${invalidText}` : ''}.`,
          }),
          events,
        };
      }
      const evaluation = conditionEvaluation(action.conditions || [], nextValues);
      const matched = evaluation.matched;
      const branch = matched ? action.then : action.else;
      const missingVariables = missingConditionVariables(action.conditions || [], nextValues);
      events.push({ type: 'if', matched, actionId: action.actionId, sourceLine: action.sourceLine, block: frame.name, missingVariables, conditionDetails: evaluation.details });
      if (missingVariables.length) {
        return {
          cursor,
          retryCursor: cursorBeforeAction,
          skipCursor: cursor,
          values: nextValues,
          finished: false,
          diagnostic: actionDiagnostic(frame, action, actionIndex, {
            kind: 'condition-missing-values',
            message: `La condicion necesita valores para ${missingVariables.join(', ')} antes de elegir una rama.`,
            missingVariables,
            conditionDetails: evaluation.details,
          }),
          events,
        };
      }
      if (Array.isArray(branch) && branch.length) cursor.frames.push(makeInlineFrame(branch, matched ? 'then' : 'else'));
      continue;
    }

    if (action.type === 'require') {
      const spec = requireSpec(action);
      if (!spec) {
        return {
          cursor,
          retryCursor: cursorBeforeAction,
          skipCursor: cursor,
          values: nextValues,
          finished: false,
          diagnostic: actionDiagnostic(frame, action, actionIndex, {
            kind: 'require-unparsed',
            message: 'REQUIRE no pudo interpretarse; se detuvo para no omitir una validacion del SST.',
          }),
          events,
        };
      }

      const evaluation = conditionEvaluation(spec.conditions, nextValues);
      const required = evaluation.matched;
      const valid = !required || hasRequiredValue(getValue(nextValues, spec.target));
      events.push({
        type: 'require',
        required,
        valid,
        target: normalizeName(spec.target),
        actionId: action.actionId,
        sourceLine: action.sourceLine,
        block: frame.name,
        conditionDetails: evaluation.details,
      });
      if (!valid) {
        return {
          cursor,
          retryCursor: cursorBeforeAction,
          skipCursor: cursor,
          values: nextValues,
          finished: false,
          diagnostic: actionDiagnostic(frame, action, actionIndex, {
            kind: 'require-failed',
            message: `El SST requiere un valor para ${normalizeName(spec.target)} antes de continuar.`,
            target: normalizeName(spec.target),
            missingVariables: [normalizeName(spec.target)],
            conditionDetails: evaluation.details,
          }),
          events,
        };
      }
      continue;
    }

    if (action.type === 'compute') {
      const missingVariables = missingComputeVariables(action, nextValues);
      if (missingVariables.length) {
        return {
          cursor,
          retryCursor: cursorBeforeAction,
          skipCursor: cursor,
          values: nextValues,
          finished: false,
          diagnostic: actionDiagnostic(frame, action, actionIndex, {
            kind: 'compute-missing-values',
            message: `El calculo no se realizo porque faltan valores para: ${missingVariables.join(', ')}.`,
            target: action.target || '',
            missingVariables,
          }),
          events,
        };
      }
    }

    const computeOperands = action.type === 'compute'
      ? Object.fromEntries(
          computeExpressionVariables(action.expression)
            .map((reference) => [reference, getValue(nextValues, reference)])
        )
      : undefined;
    if (applyLocalAction(action, nextValues, program.variableDefinitions)) {
      const assignedValue = getValue(nextValues, action.target);
      appendVariableTrace(nextValues, action.target, {
        type: action.type,
        block: frame.name,
        sourceLine: action.sourceLine ?? action.line ?? null,
        actionId: action.actionId,
        value: assignedValue,
      });
      events.push({
        type: action.type,
        target: action.target,
        expression: action.expression,
        operands: computeOperands,
        value: assignedValue,
        actionId: action.actionId,
        sourceLine: action.sourceLine,
        block: frame.name,
      });
      continue;
    }

    if (['command', 'remark', 'ssr'].includes(action.type)) {
      if (isLocalEmulatorControl(action)) {
        const controls = [...String(action.template || '').matchAll(/\{(CLEAR|CLEAR_ALL|RESET|ENTER|TAB|ESC)\}/gi)]
          .map((match) => match[1].toUpperCase());
        events.push({
          type: 'emulator-control',
          target: action.template || '',
          control: controls.join(' + '),
          controls,
          skipped: true,
          actionId: action.actionId,
          sourceLine: action.sourceLine,
          block: frame.name,
        });
        continue;
      }
      const missingVariables = missingCommandVariables(action, nextValues);
      if (missingVariables.length) {
        return {
          cursor,
          retryCursor: cursorBeforeAction,
          skipCursor: cursor,
          values: nextValues,
          finished: false,
          diagnostic: actionDiagnostic(frame, action, actionIndex, {
            kind: 'command-missing-values',
            message: `El comando no se envio porque faltan valores para construirlo: ${missingVariables.join(', ')}.`,
            target: action.template || '',
            missingVariables,
          }),
          events,
        };
      }
      const fragmentTemplate = String(action.template || '');
      const bufferedTemplate = String(nextValues[COMMAND_BUFFER_KEY] || '');
      const hasCompoundSeparator = fragmentTemplate.includes('\u0015');

      if (action.submit === false && !hasCompoundSeparator) {
        nextValues[COMMAND_BUFFER_KEY] = bufferedTemplate + fragmentTemplate;
        events.push({
          type: 'command-buffer',
          target: fragmentTemplate,
          bufferedTemplate: nextValues[COMMAND_BUFFER_KEY],
          actionId: action.actionId,
          sourceLine: action.sourceLine,
          block: frame.name,
        });
        continue;
      }

      const executableTemplate = bufferedTemplate + fragmentTemplate;
      if (!executableTemplate.trim()) {
        delete nextValues[COMMAND_BUFFER_KEY];
        events.push({
          type: 'emulator-control',
          target: action.template || '',
          control: 'ENTER',
          controls: ['ENTER'],
          skipped: true,
          actionId: action.actionId,
          sourceLine: action.sourceLine,
          block: frame.name,
        });
        continue;
      }

      const serviceAction = {
        ...action,
        template: executableTemplate,
        originalTemplate: action.template || '',
        flushCommandBuffer: Boolean(bufferedTemplate),
        block: frame.name,
      };
      events.push({ type: action.type, target: action.target, actionId: action.actionId });
      return { cursor, retryCursor: cursorBeforeAction, values: nextValues, finished: false, serviceRequest: serviceAction, events };
    }
    if (action.type === 'write') {
      const missingVariables = missingWriteVariables(action, nextValues);
      if (missingVariables.length) {
        return {
          cursor,
          retryCursor: cursorBeforeAction,
          skipCursor: cursor,
          values: nextValues,
          finished: false,
          diagnostic: actionDiagnostic(frame, action, actionIndex, {
            kind: 'write-missing-values',
            message: `WRITE no se ejecuto porque faltan valores para: ${missingVariables.join(', ')}.`,
            target: action.expression || action.template || '',
            missingVariables,
          }),
          events,
        };
      }
    }
    if (['read', 'write'].includes(action.type)) {
      events.push({ type: action.type, target: action.target, actionId: action.actionId });
      return { cursor, retryCursor: cursorBeforeAction, values: nextValues, finished: false, serviceRequest: { ...action, block: frame.name }, events };
    }
    return {
      cursor,
      retryCursor: cursorBeforeAction,
      skipCursor: cursor,
      values: nextValues,
      finished: false,
      diagnostic: actionDiagnostic(frame, action, actionIndex, {
        kind: 'unsupported-action',
        message: `La accion SST "${action.type || '(sin tipo)'}" no tiene un ejecutor compatible.`,
      }),
      events,
    };
  }

  return {
    cursor,
    values: nextValues,
    finished: false,
    error: `El motor detuvo la ejecucion despues de ${MAX_AUTOMATIC_STEPS} pasos automaticos.`,
    retryCursor: initialCursor,
    skipCursor: cursor,
    diagnostic: {
      kind: 'step-limit',
      message: `Se detecto un posible ciclo: ${MAX_AUTOMATIC_STEPS} acciones automaticas sin mostrar una ventana.`,
      block: cursor.frames[cursor.frames.length - 1]?.name || '',
      actionIndex: cursor.frames[cursor.frames.length - 1]?.index ?? null,
      actionType: '',
      actionId: '',
      sourceLine: null,
      raw: '',
      target: '',
      missingVariables: [],
    },
    events,
  };
}

export function getMenuActions(program = {}, item = {}) {
  if (item.actionRef && Array.isArray(program.menuActions?.[item.actionRef])) return program.menuActions[item.actionRef];
  return [item.action, ...(item.actions || [])]
    .filter(Boolean)
    .filter((action) => action.type !== 'submenu')
    .map((action) => (action.type === 'window' ? { ...action, type: 'showWindow' } : action));
}


